import DOMPurify from 'dompurify';
import { hasMath, unrenderMathInDom, renderMathInHtmlString, katexCssForIframe, rasterizeMathInHtml } from './aiDocMath';

// AI document → print/PDF + clipboard helpers (browser-only). Ported from the R&D
// app's lib/doc-export.js. Renders the block model (title/meta/heading/para/sig) into
// a clean, isolated iframe (hex colours + a Hind Siliguri @import for Bangla) so
// html2canvas never meets the app's Tailwind oklch colours. Used by the wizard's
// Step 3 and the document detail page.

const escapeHtml = (s) =>
  String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Block text may carry a SMALL allow-list of inline formatting written by the block
// editor (bold / italic / underline). Sanitize everything else away: text nodes are
// escaped, b/strong → <b>, i/em → <i>, u → <u>, and <br>/<div>/<p> boundaries become
// `breaks` ('\n' for storage/plain contexts, '<br/>' for HTML contexts).
const ALLOWED_INLINE = { B: 'b', STRONG: 'b', I: 'i', EM: 'i', U: 'u' };
export function sanitizeRichText(html, { breaks = '\n' } = {}) {
  const s = String(html ?? '');
  if (typeof document === 'undefined' || !/[<>]/.test(s)) return s;
  const tpl = document.createElement('template');
  tpl.innerHTML = s;
  const walk = (node) => {
    let out = '';
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === 3) out += escapeHtml(child.nodeValue);
      else if (child.nodeType === 1) {
        const tag = child.tagName;
        if (tag === 'BR') out += breaks;
        else if (ALLOWED_INLINE[tag]) { const t = ALLOWED_INLINE[tag]; out += `<${t}>${walk(child)}</${t}>`; }
        else if (tag === 'DIV' || tag === 'P') { const inner = walk(child); out += out && inner ? breaks + inner : inner; }
        else out += walk(child);
      }
    }
    return out;
  };
  return walk(tpl.content);
}

// Unfilled placeholders read as "[Label]" in state — show them inline in exports.
export const stripBrackets = (s) => String(s).replace(/\[([^\]]+)\]/g, "$1");

// Full-document HTML sanitizer for AI-generated form/table documents. Unlike
// sanitizeRichText (inline b/i/u only), this permits block + table structure so a
// reference form can be reproduced, while stripping scripts / handlers / <style>.
const DOC_HTML_CONFIG = {
  ALLOWED_TAGS: ['div', 'p', 'span', 'br', 'hr', 'strong', 'b', 'em', 'i', 'u', 's',
    'sup', 'sub', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'col', 'colgroup', 'caption', 'img'],
  ALLOWED_ATTR: ['style', 'class', 'border', 'colspan', 'rowspan', 'align', 'valign', 'width', 'height', 'src', 'alt'],
  ALLOWED_URI_REGEXP: /^(?:data:image\/(?:png|jpe?g|webp|gif)|https?:)/i,
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'link', 'meta', 'base'],
  FORBID_ATTR: ['srcset'],
};
// `preserveStyles` — keep every inline font-size / font-family / line-height as-authored.
// Used for TEMPLATE-SET (and any hand-designed) documents so the admin's exact typography
// from R2 survives (big centered heading, small subtitle…). Default false strips those so
// the AI-doc sidebar Font / Font-size controls take effect on AI-generated docs.
export function sanitizeDocHtml(html, { preserveStyles = false } = {}) {
  if (!html) return '';
  try {
    // Rendered KaTeX formulas (editor display spans) go back to their raw `$tex$` text FIRST —
    // DOMPurify would otherwise strip the data-tex source and keep only the glyph markup, so the
    // stored / exported html always holds the TeX, never the rendering.
    if (typeof document !== 'undefined' && String(html).includes('adoc-math')) {
      const t = document.createElement('div');
      t.innerHTML = String(html);
      unrenderMathInDom(t);
      html = t.innerHTML;
    }
    const clean = DOMPurify.sanitize(String(html), DOC_HTML_CONFIG);
    if (typeof document === 'undefined') return clean;
    const wrap = document.createElement('div');
    wrap.innerHTML = clean;
    // Placeholder chips are DISPLAY-ONLY — strip them so the stored / exported html keeps
    // just the literal [Label] text (the editor re-wraps them via markPlaceholders).
    wrap.querySelectorAll('span.adoc-ph').forEach((s) => s.replaceWith(document.createTextNode(s.textContent || '')));
    // Page-gap spacers are DISPLAY-ONLY sheet separators injected by the stacked editor.
    wrap.querySelectorAll('.adoc-pagegap').forEach((s) => s.remove());
    // Strip inline font-family / font-size / line-height so the document-wide Font +
    // Font-size controls actually take effect (otherwise the AI's per-element font
    // styles override them and the sidebar controls appear to do nothing). EXCEPTION:
    // spans/blocks the editor tags `.adoc-font` are DELIBERATE per-selection/paragraph
    // overrides (the inline toolbar's Font + Size) — keep their font so they persist.
    // Skipped entirely when preserveStyles (template-set docs keep their exact design).
    if (!preserveStyles) {
      wrap.querySelectorAll('[style]').forEach((el) => {
        const keepFont = el.classList && el.classList.contains('adoc-font');
        if (!keepFont) {
          el.style.removeProperty('font-family');
          el.style.removeProperty('font-size');
          el.style.removeProperty('font');
        }
        el.style.removeProperty('line-height');
      });
    }
    // Normalize tables — trim stray trailing EMPTY cells that overflow the header's leaf-
    // column count (the AI sometimes emits extra cells → broken thin columns on the right).
    wrap.querySelectorAll('table').forEach((table) => {
      const rows = Array.from(table.rows);
      if (rows.length < 2) return;
      const span = (tr) => Array.from(tr.cells).reduce((n, c) => n + (parseInt(c.getAttribute('colspan'), 10) || 1), 0);
      const expected = Math.max(span(rows[0]), span(rows[1] || rows[0]));
      if (!expected) return;
      rows.forEach((tr) => {
        while (tr.cells.length > 1 && span(tr) > expected) {
          const last = tr.cells[tr.cells.length - 1];
          if (last.textContent.trim() === '' && !last.querySelector('img')) last.remove();
          else break;
        }
      });
    });
    return wrap.innerHTML;
  } catch {
    return '';
  }
}

// Plain-text projection of a document HTML string (for the Copy action).
export function htmlToText(html) {
  const clean = sanitizeDocHtml(html);
  if (typeof document === 'undefined') {
    return String(clean).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  const el = document.createElement('div');
  el.innerHTML = clean;
  el.querySelectorAll('td, th').forEach((n) => n.append('\t'));
  el.querySelectorAll('tr, p, div, br, li, h1, h2, h3, h4, caption').forEach((n) => n.append('\n'));
  return (el.textContent || '')
    .replace(/\t\n/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// Shared CSS for HTML-format (form/table) documents, scoped to a selector so the
// on-screen preview (.adoc-html) and the print/PDF page (.adoc-page) render IDENTICALLY
// — that parity is what keeps the exported PDF matching the preview. Borders are drawn
// ONLY on tables the AI marks ruled (class="ruled" or border="1"); plain <table>s are
// used for invisible layout/alignment and stay borderless.
export function docHtmlCss(scope) {
  return `
${scope} p { margin: 2px 0; }
/* Math: formulas (rendered KaTeX spans in the preview/print, PNG <img>s in the PDF) are tall — give
   them breathing room so stacked fractions of neighbouring lines never touch, and space lines that carry math. */
${scope} .adoc-math, ${scope} .adoc-math-img { margin: 7px 3px; }
${scope} p:has(.adoc-math), ${scope} p:has(.adoc-math-img), ${scope} div:has(> .adoc-math), ${scope} div:has(> .adoc-math-img) { line-height: 1.75; margin-top: 6px; margin-bottom: 6px; }
${scope} h1, ${scope} h2, ${scope} h3, ${scope} h4 { margin: 6px 0 3px; font-weight: 700; line-height: 1.25; }
${scope} ol, ${scope} ul { margin: 3px 0; padding-left: 20px; }
${scope} table { border-collapse: collapse; width: 100%; margin: 4px 0; }
/* break-word (NOT overflow-wrap:anywhere): 'anywhere' shrinks a cell's min-content to 1
   char, so an auto-layout table lets a column collapse and long text stacks one letter per
   line (e.g. a name landing in the narrow serial column). break-word still wraps a runaway
   token to avoid overflow, but sizes columns to the longest WORD. Keep it this way. */
${scope} td, ${scope} th { padding: 2px 5px; vertical-align: top; overflow-wrap: break-word; word-break: normal; }
${scope} th { font-weight: 600; text-align: center; }
${scope} table.ruled, ${scope} table.ruled td, ${scope} table.ruled th,
${scope} table[border="1"], ${scope} table[border="1"] td, ${scope} table[border="1"] th { border: 1px solid #222; }
${scope} tr, ${scope} td, ${scope} th { break-inside: avoid; }
${scope} img { max-width: 100%; }
${scope} .adoc-ph { background: #fff3cd; color: #8a6d00; border-radius: 3px; padding: 0 3px; box-decoration-break: clone; }
`;
}

// Per-page top/bottom margin (px @96dpi) for HTML-format documents. Reserved on EVERY page
// (not just the first) so a document that spills onto page 2+ keeps a header/footer gap.
// Shared by the preview, the PDF slicer, and the print @page rule so all three agree.
export const DOC_PAGE_MARGIN_Y = 40;

// Resolve the user's page-margin setting to per-side px. `margins` is {top,bottom,left,right}
// in px or null (null = the legacy defaults, which differ per doc kind/orientation). Every
// renderer — preview, PDF, print — resolves through this so page geometry always agrees.
export function resolveMargins(margins, { landscape = false, html = false } = {}) {
  const defV = html ? DOC_PAGE_MARGIN_Y : 48;
  const defH = html ? 40 : (landscape ? 130 : 56);
  const n = (v, d) => (Number.isFinite(v) && v >= 0 ? Math.round(v) : d);
  return {
    top: n(margins?.top, defV),
    bottom: n(margins?.bottom, defV),
    left: n(margins?.left, defH),
    right: n(margins?.right, defH),
  };
}

// Work out where each page STARTS (Y offset in px, relative to `root`'s content box) so no
// table row or block element is ever split across a page. `usableH` = page height minus the
// top+bottom margins. Returns [0, y1, y2, …]; length === page count. Break units are table
// ROWS (a table splits between its rows) and non-table top-level blocks (kept whole). A unit
// taller than a full page is left to overflow/split on its own page rather than lost.
export function computeHtmlPageBreaks(root, usableH) {
  if (!root || !(usableH > 0)) return [0];
  const rootTop = root.getBoundingClientRect().top;
  const units = [];
  const push = (el) => {
    // Explicit page-break markers — honoured even at zero height. `.adoc-break` joins
    // template-set bundle docs; `.doc-page-break` is what the manual Lexical editor
    // emits inside a single doc (PageBreakNode.exportDOM), so a source with page breaks
    // paginates identically here.
    const pb = !!(el.classList && (el.classList.contains('adoc-break') || el.classList.contains('doc-page-break')));
    const r = el.getBoundingClientRect();
    if (r.height > 0 || pb) units.push({ top: r.top - rootTop, bottom: r.bottom - rootTop, pb });
  };
  const walk = (el) => {
    for (const child of el.children) {
      if (child.tagName === 'TABLE') {
        const rows = child.querySelectorAll('tr');
        if (rows.length) rows.forEach(push); else push(child);
      } else if (/^(THEAD|TBODY|TFOOT|DIV|SECTION|ARTICLE)$/.test(child.tagName) && child.querySelector('table, tr')) {
        walk(child); // descend into wrappers that contain tables so we still break by row
      } else {
        push(child);
      }
    }
  };
  walk(root);
  units.sort((a, b) => a.top - b.top);
  const breaks = [0];
  let pageStart = 0;
  for (const u of units) {
    // Explicit marker → force a new page at its position.
    if (u.pb) {
      if (u.top > pageStart + 1) { pageStart = u.top; breaks.push(pageStart); }
      continue;
    }
    // Doesn't fit on the current page (and isn't the unit that opens it) → start a new page.
    if (u.bottom - pageStart > usableH + 1 && u.top > pageStart + 1) {
      pageStart = u.top;
      breaks.push(pageStart);
    }
  }
  return breaks;
}

// Per-block font choices offered by the editor. Ids are stored on the block
// (`block.font`); stacks are shared by the preview and the PDF/print export.
export const DOC_FONTS = [
  { id: 'kalpurush', label: 'Kalpurush', labelBn: 'কালপুরুষ', stack: "'Kalpurush','Noto Serif Bengali',serif" },
  { id: 'solaiman', label: 'SolaimanLipi', labelBn: 'সোলাইমানলিপি', stack: "'SolaimanLipi','Kalpurush',serif" },
  { id: 'hind', label: 'Hind Siliguri', labelBn: 'হিন্দ সিলিগুরি', stack: "'Hind Siliguri','Kalpurush',sans-serif" },
  { id: 'georgia', label: 'Georgia', labelBn: 'জর্জিয়া', stack: "Georgia,'Times New Roman',serif" },
  { id: 'times', label: 'Times', labelBn: 'টাইমস', stack: "'Times New Roman',Times,serif" },
  { id: 'arial', label: 'Arial', labelBn: 'এরিয়াল', stack: 'Arial,Helvetica,sans-serif' },
];
export const docFontStack = (id) => DOC_FONTS.find((f) => f.id === id)?.stack || null;

// Page sizes offered by the document-settings panel. `px` = CSS px @96dpi (drives the
// live preview + the PDF render iframe); `css` = the CSS @page `size` keyword (print);
// jsPDF accepts the id as its `format`. Shared by the preview and the export.
export const PAPER_SIZES = [
  { id: 'a4', label: 'A4', px: [794, 1123], css: 'A4', dim: '210 × 297 mm' },
  { id: 'letter', label: 'Letter', px: [816, 1056], css: 'Letter', dim: '8.5 × 11 in' },
  { id: 'legal', label: 'Legal', px: [816, 1344], css: 'Legal', dim: '8.5 × 14 in' },
  { id: 'a5', label: 'A5', px: [559, 794], css: 'A5', dim: '148 × 210 mm' },
];
export function paperPx(id, landscape) {
  const p = PAPER_SIZES.find((x) => x.id === id) || PAPER_SIZES[0];
  const [w, h] = p.px;
  return landscape ? { w: h, h: w } : { w, h };
}

export const hasBangla = (s) => /[ঀ-৿]/.test(String(s || ""));

export function blocksToText(blocks) {
  return (blocks || [])
    .map((b) => stripBrackets(sanitizeRichText(b.text, { breaks: '\n' }).replace(/<[^>]+>/g, '')))
    .join("\n\n");
}

// Build a standalone printable HTML document from blocks.
// Default font size (px) per block kind — mirrors the `styles` below. A per-block
// `fontSize` overrides its kind default; `fontScale` scales the whole document.
const KIND_FONT_PX = { title: 27, meta: 11, heading: 12.5, para: 15.5, sig: 15 };

export function buildPrintHtml(blocks, { title = "Document", isBn = false, logo = null, sans = false, paper = 'a4', landscape = false, globalFont = null, fontScale = 1, bodyHtml = null, forPrint = false, margins = null, preserveStyles = false } = {}) {
  const bnFonts = isBn ? ",'Kalpurush','Noto Serif Bengali'" : "";
  const pageCss = (PAPER_SIZES.find((p) => p.id === paper) || PAPER_SIZES[0]).css;
  const globalStack = docFontStack(globalFont); // document-wide font (per-block override wins)
  // Kalpurush is bundled in the app (public/fonts). The export renders in an isolated
  // iframe, so declare the @font-face with an absolute URL so the iframe can load it.
  const origin = (typeof window !== 'undefined' && window.location && window.location.origin) || '';
  const fontImport = isBn
    ? `@import url('https://fonts.googleapis.com/css2?family=Hind+Siliguri:wght@400;500;600;700&display=swap');
@font-face{font-family:'Kalpurush';src:url('${origin}/fonts/Kalpurush.woff2') format('woff2'),url('${origin}/fonts/Kalpurush.woff') format('woff');font-weight:400;font-style:normal;font-display:swap;}
@font-face{font-family:'SolaimanLipi';src:url('${origin}/fonts/SolaimanLipiNormal.woff2') format('woff2'),url('${origin}/fonts/SolaimanLipiNormal.woff') format('woff');font-weight:400;font-style:normal;font-display:swap;}`
    : "";
  const styles = {
    title: "font-size:27px;font-weight:600;text-align:center;text-transform:uppercase;letter-spacing:0.03em;color:#1a1a1a;margin:0 0 2px;",
    meta: `font-family:'SF Mono',Menlo,monospace${bnFonts};font-size:11px;text-align:center;letter-spacing:0.12em;text-transform:uppercase;color:#9a9a93;margin:2px 0 26px;`,
    heading: `font-family:Arial,Helvetica,sans-serif${bnFonts};font-size:12.5px;font-weight:600;text-transform:uppercase;letter-spacing:0.06em;color:#1a1a1a;margin:24px 0 2px;`,
    para: "font-size:15.5px;line-height:1.75;white-space:pre-line;color:#2c2c29;margin:0 0 4px;",
    sig: "font-size:15px;line-height:1.9;white-space:pre-line;color:#2c2c29;margin:30px 0 0;",
  };
  const serif = (globalStack || (sans ? "Arial,Helvetica,sans-serif" : "Georgia,'Times New Roman',serif")) + bnFonts;
  const logoHtml = logo
    ? `<img src="${logo}" alt="letterhead" style="display:block;height:54px;max-width:200px;object-fit:contain;margin-bottom:30px;" />`
    : "";
  let body = bodyHtml
    ? sanitizeDocHtml(bodyHtml, { preserveStyles })
    : (blocks || [])
    .map((b) => {
      // Per-block overrides from the editor: alignment + font family.
      let style = styles[b.kind] || styles.para;
      if (b.align && ['left', 'center', 'right', 'justify'].includes(b.align)) style += `text-align:${b.align};`;
      const stack = docFontStack(b.font);
      if (stack) style += `font-family:${stack}${isBn ? bnFonts : ''};`;
      // Effective size = per-block override (or the kind default) × document scale.
      const baseSize = (typeof b.fontSize === 'number' && b.fontSize > 0) ? b.fontSize : (KIND_FONT_PX[b.kind] || KIND_FONT_PX.para);
      style += `font-size:${Math.round(baseSize * (fontScale || 1) * 100) / 100}px;`;
      return `<div style="${style}">${stripBrackets(sanitizeRichText(b.text, { breaks: b.kind === 'sig' ? '\n' : '<br/>' }))}</div>`;
    })
    .join("");
  // Math ($...$ / $$...$$) in HTML docs: PRINT bakes live KaTeX into the document (native browser
  // rendering is exact). The PDF path passes formula <img>s instead (see downloadDocPdf), so the
  // body has no `$..$` left by then and no KaTeX CSS is added.
  const withKatex = !!(forPrint && bodyHtml && hasMath(body));
  if (withKatex) body = renderMathInHtmlString(body);
  // The document is wrapped in a fixed-size white ".adoc-page" (paper dimensions +
  // margins), centered on a light backdrop — this mirrors the on-screen preview, so the
  // print/PDF output looks identical instead of raw edge-to-edge text on a dark window.
  const { w: PW, h: PH } = paperPx(paper, landscape);
  const M = resolveMargins(margins, { landscape, html: !!bodyHtml });
  // Print uses NATIVE paged media for BOTH doc kinds: real @page margins on every page;
  // break-inside:avoid keeps table rows (HTML docs) and whole blocks (block docs) from
  // being sliced — the same block-boundary rule as the preview and the PDF slicer. The
  // PDF path instead keeps a fixed .adoc-page (no vertical padding) and slices it per
  // row/block-boundary page with margin bands.
  const nativePaged = !!(forPrint && bodyHtml);
  const nativeBlocks = !!(forPrint && !bodyHtml);
  return `<!doctype html><html><head><meta charset="utf-8" />
<title>${escapeHtml(title || "Document")}</title>
<style>
  ${fontImport}
  ${withKatex ? katexCssForIframe() : ''}
  @page { size: ${pageCss} ${landscape ? 'landscape' : 'portrait'}; margin: ${nativePaged ? `${M.top}px ${M.right}px ${M.bottom}px ${M.left}px` : '0'}; }
  * { -webkit-print-color-adjust:exact; print-color-adjust:exact; box-sizing:border-box; }
  html,body { margin:0; background:#f4f4f5; }
  .adoc-page {
    width:${PW}px; min-height:${PH}px; margin:0 auto; background:#fff;
    padding:${M.top}px ${M.right}px ${M.bottom}px ${M.left}px; font-family:${serif}; color:#2c2c29;
  }
  ${nativeBlocks ? `.adoc-page > div{break-inside:avoid;page-break-inside:avoid;}` : ''}
  ${bodyHtml ? `.adoc-page{font-size:${Math.round(14 * (fontScale || 1) * 100) / 100}px;line-height:1.45;padding:0 ${M.right}px 0 ${M.left}px;${nativePaged ? 'width:auto;min-height:0;padding:0;' : ''}}${docHtmlCss('.adoc-page')}` : ''}
  .adoc-break, .doc-page-break { break-after:page; page-break-after:always; height:0; overflow:hidden; }
  @media screen { body { padding:24px; } .adoc-page { box-shadow:0 2px 16px rgba(0,0,0,0.12); } }
  @media print { html,body { background:#fff; } .adoc-page { box-shadow:none; margin:0; } }
</style></head>
<body><div class="adoc-page">${logoHtml}${body}</div></body></html>`;
}

const safeFilename = (s) =>
  (String(s || "document").trim().replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").slice(0, 80) || "document");

// Render the document into a hidden iframe and save it straight to a PDF file
// (no print tab). The iframe carries only our own clean styles (hex colours +
// a Hind Siliguri @import for Bangla), so html2canvas never meets Tailwind's
// oklch colours. Libraries are lazy-loaded to keep them out of the main bundle.
export async function downloadDocPdf(blocks, opts = {}) {
  const { filename = "document", isBn = false, logo = null, sans = false, paper = 'a4', landscape = false, globalFont = null, fontScale = 1, bodyHtml = null, margins = null, preserveStyles = false } = opts;
  const [jspdfMod, h2cMod] = await Promise.all([
    import("jspdf"),
    import("html2canvas"),
  ]);
  const jsPDF = jspdfMod.jsPDF || jspdfMod.default;
  const html2canvas = h2cMod.default || h2cMod;

  const { w: PW, h: PH } = paperPx(paper, landscape); // render dims in CSS px @96dpi
  // html2canvas can't draw KaTeX fractions, so each formula is rasterised to a PNG <img> (sized in
  // em → it scales with the page's fit-to-page font shrink) BEFORE the html is built.
  const pdfBodyHtml = bodyHtml && hasMath(sanitizeDocHtml(bodyHtml, { preserveStyles }))
    ? await rasterizeMathInHtml(sanitizeDocHtml(bodyHtml, { preserveStyles }), { fontPx: 14 * (fontScale || 1) })
    : bodyHtml;
  const html = buildPrintHtml(blocks, { isBn, logo, sans, title: filename, paper, landscape, globalFont, fontScale, bodyHtml: pdfBodyHtml, margins, preserveStyles });
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = `position:fixed;left:-9999px;top:0;width:${PW}px;border:0;`;
  document.body.appendChild(iframe);
  try {
    const idoc = iframe.contentDocument;
    idoc.open();
    idoc.write(html);
    idoc.close();
    // Capture the white ".adoc-page" itself (it already carries the paper size + margins).
    idoc.body.style.cssText += ";padding:0;background:#fff;";

    await new Promise((res) => {
      if (idoc.readyState === "complete") res();
      else iframe.onload = res;
    });
    try { await idoc.fonts.ready; } catch (e) {}
    await new Promise((r) => setTimeout(r, isBn ? 450 : 150)); // let webfonts/logo paint

    const page = idoc.querySelector('.adoc-page') || idoc.body;
    const pdf = new jsPDF({ unit: "px", format: paper, orientation: landscape ? "landscape" : "portrait", compress: true });
    const pw = pdf.internal.pageSize.getWidth();
    const ph = pdf.internal.pageSize.getHeight();

    if (bodyHtml) {
      const MH = resolveMargins(margins, { landscape, html: true });
      const MY = MH.top; // top margin band; bottom uses MH.bottom
      const U = Math.max(120, PH - MH.top - MH.bottom); // usable content height per page
      // HTML forms: fit onto ONE full-width page when within ~1.5 USABLE pages by shrinking
      // the font (keeps full width, no distortion, no side gaps).
      const baseFs = 14 * (fontScale || 1);
      page.style.fontSize = `${baseFs}px`;
      if (page.scrollHeight > U && page.scrollHeight <= U * 1.5) {
        let fs = baseFs;
        let guard = 0;
        while (page.scrollHeight > U + 2 && fs > 9 && guard++ < 80) { fs -= 0.3; page.style.fontSize = `${fs}px`; }
      }
      // Break pages at ROW/element boundaries (no table row is ever split) and reserve a
      // top+bottom margin band on every page. Mirrors the on-screen editor exactly.
      const totalH = page.scrollHeight;
      const breaks = computeHtmlPageBreaks(page, U);
      const clip = idoc.createElement('div');
      clip.style.cssText = `position:relative;width:${PW}px;height:${PH}px;overflow:hidden;background:#fff;`;
      page.parentNode.insertBefore(clip, page);
      clip.appendChild(page);
      page.style.position = 'relative';
      page.style.zIndex = '1';
      // Opaque white bands mask any row that bleeds into the top/bottom margin of this page.
      const bandTop = idoc.createElement('div');
      bandTop.style.cssText = `position:absolute;left:0;right:0;top:0;height:${MY}px;background:#fff;z-index:2;`;
      const bandBot = idoc.createElement('div');
      bandBot.style.cssText = `position:absolute;left:0;right:0;background:#fff;z-index:2;`;
      clip.appendChild(bandTop);
      clip.appendChild(bandBot);
      for (let i = 0; i < breaks.length; i++) {
        // Shift so page i's first row sits just below the top margin; the bottom band starts
        // where this page's content ends so nothing from the next page peeks through.
        page.style.margin = `${MY - breaks[i]}px 0 0 0`;
        const yEnd = MY + ((i + 1 < breaks.length ? breaks[i + 1] : totalH) - breaks[i]);
        bandBot.style.top = `${Math.min(yEnd, PH)}px`;
        bandBot.style.height = `${Math.max(0, PH - Math.min(yEnd, PH))}px`;
        // eslint-disable-next-line no-await-in-loop
        const canvas = await html2canvas(clip, {
          scale: 2, backgroundColor: '#ffffff', useCORS: true,
          windowWidth: PW, width: PW, height: PH,
        });
        if (i > 0) pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, pw, ph, undefined, 'FAST');
      }
    } else {
      // Block-format docs: break BETWEEN blocks — the same rule the on-screen preview and
      // native print use — so no text line is ever sliced mid-letter. The page's own
      // vertical padding is removed and re-created per page as opaque margin bands.
      const MB = resolveMargins(margins, { landscape, html: false });
      const PADY = MB.top; // top margin band; bottom margin = MB.bottom via U
      const U = Math.max(120, PH - MB.top - MB.bottom); // usable content height (matches the preview)
      page.style.paddingTop = '0px';
      page.style.paddingBottom = '0px';
      page.style.minHeight = '0px';
      page.style.position = 'relative';
      const kids = Array.from(page.children);
      const totalH = page.scrollHeight;
      const breaks = [0];
      let start = 0;
      for (let k = 0; k < kids.length; k++) {
        const top = kids[k].offsetTop;
        const end = k + 1 < kids.length ? kids[k + 1].offsetTop : totalH;
        if (top > start && end - start > U) { breaks.push(top); start = top; }
        // A single block taller than a whole page: fall back to hard cuts inside it.
        while (end - start > U) { start += U; if (start < end) breaks.push(start); }
      }
      const clip = idoc.createElement('div');
      clip.style.cssText = `position:relative;width:${PW}px;height:${PH}px;overflow:hidden;background:#fff;`;
      page.parentNode.insertBefore(clip, page);
      clip.appendChild(page);
      page.style.zIndex = '1';
      const bandTop = idoc.createElement('div');
      bandTop.style.cssText = `position:absolute;left:0;right:0;top:0;height:${PADY}px;background:#fff;z-index:2;`;
      const bandBot = idoc.createElement('div');
      bandBot.style.cssText = `position:absolute;left:0;right:0;background:#fff;z-index:2;`;
      clip.appendChild(bandTop);
      clip.appendChild(bandBot);
      for (let i = 0; i < breaks.length; i++) {
        page.style.margin = `${PADY - breaks[i]}px 0 0 0`;
        const yEnd = PADY + ((i + 1 < breaks.length ? breaks[i + 1] : totalH) - breaks[i]);
        bandBot.style.top = `${Math.min(yEnd, PH)}px`;
        bandBot.style.height = `${Math.max(0, PH - Math.min(yEnd, PH))}px`;
        // eslint-disable-next-line no-await-in-loop
        const canvas = await html2canvas(clip, {
          scale: 2, backgroundColor: '#ffffff', useCORS: true,
          windowWidth: PW, width: PW, height: PH,
        });
        if (i > 0) pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, pw, ph, undefined, 'FAST');
      }
    }
    pdf.save(`${safeFilename(filename)}.pdf`);
  } finally {
    document.body.removeChild(iframe);
  }
}

// Print the document via a hidden, isolated iframe (NOT a popup window). A popup opened
// with about:blank rendered as a raw, dark, edge-to-edge page and was flaky; the iframe
// carries the same white ".adoc-page" markup the preview uses, waits for fonts/logo, then
// prints only itself — so the print/preview shows the exact document. Async: await it to
// drive a loading state. Resolves true on success, false on error.
export async function openPrint(blocks, opts = {}) {
  const { isBn = false, paper = 'a4', landscape = false, fontScale = 1, bodyHtml = null, margins = null, preserveStyles = false } = opts;
  const html = buildPrintHtml(blocks, { ...opts, forPrint: true });
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = "position:fixed;left:-9999px;top:0;width:900px;height:1200px;border:0;";
  document.body.appendChild(iframe);
  const cleanup = () => setTimeout(() => { try { document.body.removeChild(iframe); } catch (e) {} }, 800);
  try {
    const idoc = iframe.contentDocument;
    idoc.open();
    idoc.write(html);
    idoc.close();

    await new Promise((res) => {
      if (idoc.readyState === "complete") res();
      else iframe.onload = res;
    });
    try { await idoc.fonts.ready; } catch (e) {}
    // Wait for the letterhead logo image (if any) to load before printing.
    const img = idoc.querySelector("img");
    if (img && !img.complete) {
      await new Promise((r) => { img.onload = r; img.onerror = r; setTimeout(r, 1500); });
    }
    await new Promise((r) => setTimeout(r, isBn ? 450 : 150)); // let webfonts settle

    // HTML forms: fit to one page (font-shrink) when within ~1.5 pages, so a single-page
    // form prints on one page instead of spilling. Longer docs paginate natively.
    if (bodyHtml) {
      const page = idoc.querySelector('.adoc-page');
      if (page) {
        // Native print reserves a top+bottom margin on every page, so "one page" of content
        // is the page height minus those margins.
        const MP = resolveMargins(margins, { landscape, html: true });
        const U = Math.max(120, paperPx(paper, landscape).h - MP.top - MP.bottom);
        const baseFs = 14 * (fontScale || 1);
        page.style.fontSize = `${baseFs}px`;
        if (page.scrollHeight > U && page.scrollHeight <= U * 1.5) {
          let fs = baseFs;
          let guard = 0;
          while (page.scrollHeight > U + 2 && fs > 9 && guard++ < 80) { fs -= 0.3; page.style.fontSize = `${fs}px`; }
        }
      }
    }

    const win = iframe.contentWindow;
    win.onafterprint = cleanup;
    win.focus();
    win.print();
    setTimeout(cleanup, 60000); // fallback if onafterprint never fires
    return true;
  } catch (e) {
    cleanup();
    return false;
  }
}
